/**
 * Central icon registry — Tabler Icons ONLY.
 *
 * Rules enforced here (do not break):
 *  - Every glyph is a STATIC named import from '@tabler/icons-react' (tree-shakeable).
 *  - No `import *`, no dynamic import, no runtime resolution against the whole package.
 *    The only lookup is `ICONS[name]` over this curated, bounded map.
 *  - Pages/components import { Icon } (or ICONS) from here — never '@tabler/icons-react'
 *    directly, never '@carbon/icons-react', never 'lucide-react'.
 *  - Icons are exposed by SEMANTIC product name (delete, export, threat, domain…),
 *    not by Tabler's technical name. Same action / same module => same icon everywhere.
 *
 * Style: outline, 1.8px stroke, rounded caps (Tabler default grid is 24, scaled
 * via the `size` prop). Filled variants are reserved for status/critical semantics
 * (success, error, warning, criticalRisk) and active/selected nav.
 *
 * Sizing tokens (px):
 *  - 16: in-table actions, small metadata, input-adornment icons   (xs/sm/md)
 *  - 20: sidebar, toolbar, buttons, dropdown actions               (lg)
 *  - 24: dashboard cards, page titles, primary module icons        (xl)
 *  - 32: empty states, large summary / onboarding                  (xxl)
 */
import React from 'react';
import {
  // navigation
  IconLayoutDashboard, IconRadar2, IconServer2, IconShieldExclamation, IconKey,
  IconRadar, IconReportAnalytics, IconBuildingSkyscraper, IconAdjustmentsAlt,
  IconPlug, IconKeyboard, IconUsers, IconSettings, IconUserShield,
  // actions
  IconPlus, IconPencil, IconTrash, IconSearch, IconFilter, IconFilterOff,
  IconRefresh, IconShare, IconDownload, IconUpload, IconCopy, IconExternalLink,
  IconLink, IconChevronDown, IconChevronUp, IconChevronRight, IconChevronLeft,
  IconChevronsLeft, IconChevronsRight,
  IconDotsVertical, IconX, IconArrowLeft, IconArrowRight, IconRotate,
  IconPlayerPause, IconPlayerPlayFilled, IconPlayerStopFilled, IconPlayerPlay,
  IconBolt, IconArrowsSort, IconColumns, IconLayoutGrid, IconList,
  // security / CTI
  IconAlertTriangle, IconBell, IconHexagonLetterX, IconShieldHalf,
  IconAlertHexagon, IconAlertCircle, IconInfoCircle, IconPassword, IconDroplet,
  IconNetwork, IconBug, IconMail, IconWorld, IconGitBranch, IconRouter,
  IconUser, IconShieldLock, IconLock, IconCertificate, IconDeviceDesktop,
  IconDeviceDesktopOff, IconDeviceLaptop, IconCoins, IconStack2, IconTrendingUp,
  IconShieldCheck,
  // data / UI states
  IconCircleCheckFilled, IconAlertTriangleFilled, IconCircleXFilled, IconBox,
  IconLoader2, IconCalendar, IconCalendarOff, IconClock, IconChartLine, IconFileText,
  IconFileTypePdf, IconTable, IconCheck, IconCircleCheck, IconBookmark,
  IconBookmarkFilled, IconSquare, IconLogout, IconFileTypeCsv, IconCircle,
  // extended
  IconArrowUp, IconArrowDown, IconActivity, IconDatabase, IconChartPie,
  IconChartBar, IconGauge, IconMinus, IconPointFilled, IconFingerprint,
  IconWallet, IconHash, IconCpu, IconAdjustments, IconEye, IconEyeOff, IconTag,
  IconSun, IconMoon, IconFocus2, IconCircleDot, IconSparkles, IconFlag,
  // industry sectors
  IconBuildingBank, IconShoppingCart, IconToolsKitchen2, IconHeartbeat,
  IconBroadcast, IconSchool, IconBuilding, IconNews, IconTruck,
  IconBuildingFactory2,
  // device / infostealer-log extras
  IconPhone, IconBrandDiscord, IconBrandSteam, IconMapPin, IconRoute, IconApps,
  // AI assistant widget
  IconMessageChatbot, IconSend,
} from '@tabler/icons-react';

/** Semantic product name -> Tabler glyph. The single source of truth. */
export const ICONS = {
  // ── Navigation ──────────────────────────────────────────────
  dashboard: IconLayoutDashboard,
  scans: IconRadar2,
  assets: IconServer2,
  findings: IconShieldExclamation,
  credentials: IconKey,
  intelligence: IconRadar,
  reports: IconReportAnalytics,
  organization: IconBuildingSkyscraper,
  rules: IconAdjustmentsAlt,
  integrations: IconPlug,
  apiKeys: IconKeyboard,
  users: IconUsers,
  settings: IconSettings,
  admin: IconUserShield,

  // ── Actions ─────────────────────────────────────────────────
  add: IconPlus,
  edit: IconPencil,
  delete: IconTrash,
  search: IconSearch,
  filter: IconFilter,
  filterClear: IconFilterOff,
  refresh: IconRefresh,
  export: IconShare,
  download: IconDownload,
  upload: IconUpload,
  copy: IconCopy,
  openExternal: IconExternalLink,
  link: IconLink,
  expand: IconChevronDown,
  collapse: IconChevronUp,
  moreActions: IconDotsVertical,
  close: IconX,
  back: IconArrowLeft,
  forward: IconArrowRight,
  retry: IconRotate,
  pause: IconPlayerPause,
  resume: IconPlayerPlayFilled,
  stop: IconPlayerStopFilled,
  play: IconPlayerPlay,
  runScan: IconRadar2,
  flash: IconBolt,
  sort: IconArrowsSort,
  chevronDown: IconChevronDown,
  chevronUp: IconChevronUp,
  chevronRight: IconChevronRight,
  chevronLeft: IconChevronLeft,
  pageFirst: IconChevronsLeft,
  pageLast: IconChevronsRight,
  columns: IconColumns,
  gridView: IconLayoutGrid,
  listView: IconList,

  // ── Security / CTI ──────────────────────────────────────────
  threat: IconAlertTriangle,
  alert: IconBell,
  criticalRisk: IconHexagonLetterX,
  highRisk: IconShieldExclamation,
  mediumRisk: IconAlertTriangle,
  lowRisk: IconAlertHexagon,
  safe: IconShieldCheck,
  exposedCredential: IconPassword,
  leak: IconDroplet,
  darkWeb: IconNetwork,
  malware: IconBug,
  aiTest: IconSparkles,
  aiVerified: IconSparkles,
  chat: IconMessageChatbot,
  send: IconSend,
  aiCopilot: IconAiMark,
  phishing: IconMail,
  domain: IconWorld,
  subdomain: IconGitBranch,
  ipAddress: IconRouter,
  asset: IconServer2,
  target: IconFocus2,
  server: IconServer2,
  endpoint: IconDeviceDesktop,
  endpointOff: IconDeviceDesktopOff,
  device: IconDeviceLaptop,
  user: IconUser,
  organizationGlyph: IconBuildingSkyscraper,
  shield: IconShieldHalf,
  shieldOff: IconShieldExclamation,
  lock: IconLock,
  key: IconKey,
  audit: IconCertificate,
  compliance: IconShieldLock,
  credits: IconCoins,
  layers: IconStack2,
  trend: IconTrendingUp,

  // ── Data / UI states ────────────────────────────────────────
  success: IconCircleCheckFilled,
  warning: IconAlertTriangleFilled,
  error: IconCircleXFilled,
  info: IconInfoCircle,
  check: IconCheck,
  emptyState: IconBox,
  noResults: IconSearch,
  loading: IconLoader2,
  calendar: IconCalendar,
  scheduleOff: IconCalendarOff,
  clock: IconClock,
  chart: IconChartLine,
  table: IconTable,
  list: IconList,
  document: IconFileText,
  report: IconReportAnalytics,
  pdf: IconFileTypePdf,
  spreadsheet: IconFileTypeCsv,
  bookmark: IconBookmark,
  bookmarkActive: IconBookmarkFilled,
  checkboxEmpty: IconSquare,
  checkmarkOutline: IconCircleCheck,
  mail: IconMail,
  at: IconMail,
  logout: IconLogout,

  // ── Extended ────────────────────────────────────────────────
  arrowUp: IconArrowUp,
  arrowDown: IconArrowDown,
  activity: IconActivity,
  database: IconDatabase,
  chartPie: IconChartPie,
  chartBar: IconChartBar,
  gauge: IconGauge,
  subtract: IconMinus,
  dot: IconPointFilled,
  circle: IconCircle,
  fingerprint: IconFingerprint,
  wallet: IconWallet,
  hash: IconHash,
  tag: IconTag,
  cpu: IconCpu,
  adjust: IconAdjustments,
  view: IconEye,
  viewOff: IconEyeOff,
  themeLight: IconSun,
  themeDark: IconMoon,
  coverage: IconCircleDot,
  alertCircle: IconAlertCircle,
  flag: IconFlag,

  // ── Industry sectors (industry-intel module) ────────────────
  sectorFinance: IconBuildingBank,
  sectorRetail: IconShoppingCart,
  sectorFood: IconToolsKitchen2,
  sectorHealth: IconHeartbeat,
  sectorTech: IconCpu,
  sectorEnergy: IconBolt,
  sectorTelecom: IconBroadcast,
  sectorEducation: IconSchool,
  sectorCorporate: IconBuilding,
  sectorMedia: IconNews,
  sectorLogistics: IconTruck,
  sectorManufacturing: IconBuildingFactory2,

  // ── Device / infostealer-log extras (breach + credential drawers) ─
  phone: IconPhone,
  discord: IconBrandDiscord,
  steam: IconBrandSteam,
  location: IconMapPin,
  infectionPath: IconRoute,
  apps: IconApps,
};

const SIZE = { xs: 16, sm: 16, md: 16, lg: 20, xl: 24, xxl: 32 };

/* Filled glyphs have no stroke geometry — passing a stroke is a no-op but we
   skip it to avoid React warnings on the underlying SVG. */
/**
 * Custom brand mark — AI assistant. Filled twin-spark glyph (large concave
 * four-point star + small echo). Tabler has no filled spark at this weight,
 * so this is the registry's single hand-drawn exception; it keeps the Tabler
 * prop contract (size / color / className / aria-*) so <Icon> treats it the
 * same as every other entry.
 */
function IconAiMark({ size = 24, color = 'currentColor', stroke, strokeWidth, ...rest }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill={color} xmlns="http://www.w3.org/2000/svg" {...rest}
    >
      <path d="M10.6 4.6c.93 4.78 3.52 7.37 8.3 8.3-4.78.93-7.37 3.52-8.3 8.3-.93-4.78-3.52-7.37-8.3-8.3 4.78-.93 7.37-3.52 8.3-8.3Z" />
      <path d="M18.2 1.9c.43 2.2 1.62 3.4 3.83 3.83-2.2.43-3.4 1.62-3.83 3.83-.43-2.2-1.62-3.4-3.83-3.83 2.2-.43 3.4-1.62 3.83-3.83Z" opacity=".55" />
    </svg>
  );
}

const FILLED = new Set(['success', 'warning', 'error', 'dot', 'resume', 'stop', 'aiCopilot']);

/**
 * <Icon name="delete" size="sm" />
 *
 * name        — semantic key from ICONS above
 * size        — token (xs|sm|md|lg|xl|xxl) or a raw number of px
 * color       — optional CSS color; folded into currentColor (prefer theme tokens)
 * strokeWidth — outline weight (default 1.8; ignored by filled glyphs)
 * Decorative by default (aria-hidden). Pass aria-label or title to expose it.
 */
export function Icon({
  name,
  size = 'md',
  color,
  className,
  style,
  title,
  strokeWidth,
  'aria-label': ariaLabel,
  ...rest
}) {
  const Cmp = ICONS[name];
  if (!Cmp) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[Icon] Unknown semantic name "${name}"`);
    }
    return null;
  }
  const px = typeof size === 'number' ? size : (SIZE[size] ?? SIZE.md);
  const labelled = Boolean(ariaLabel || title);
  const strokeProps = FILLED.has(name) ? {} : { stroke: strokeWidth ?? 1.8 };
  return (
    <Cmp
      size={px}
      color={color || 'currentColor'}
      className={className}
      style={style}
      title={title}
      aria-label={ariaLabel}
      aria-hidden={labelled ? undefined : true}
      {...strokeProps}
      {...rest}
    />
  );
}

export default Icon;
